/**
 * Pure decision logic for the segmented quality control (issue #73): the
 * Auto | Low | Med | High selector that replaced the binary quality toggle.
 * Kept free of DOM / renderer references so the label, active-state and
 * switch-planning rules are unit-tested in Node (main.ts owns the DOM wiring).
 */
import type { QualityTier } from '../core/quality';

/** A user selection in the control. `auto` follows auto-detection (no persisted
 * override); the three tiers are explicit, persisted choices. */
export type QualityChoice = 'auto' | QualityTier;

/** Control order, left → right. */
export const QUALITY_CHOICES: readonly QualityChoice[] = ['auto', 'low', 'medium', 'high'];

/** Short button label for a tier. */
export function tierTitle(tier: QualityTier): string {
  return tier === 'low' ? 'Low' : tier === 'medium' ? 'Med' : 'High';
}

/**
 * The visible label for a choice's button. `auto` shows the tier auto-detection
 * actually resolved to — e.g. "Auto (High)" — so a user sees what they got
 * rather than a bare "Auto".
 */
export function choiceLabel(choice: QualityChoice, autoDetectedTier: QualityTier): string {
  return choice === 'auto' ? `Auto (${tierTitle(autoDetectedTier)})` : tierTitle(choice);
}

/**
 * Which button should read as active on boot. A URL `?quality=` override or a
 * persisted choice highlights the tier actually rendering; with neither, the
 * control is in Auto mode. (A URL override highlights its tier even though it is
 * session-only and never persisted — it is what the user is looking at.)
 */
export function initialChoice(params: {
  urlOverride: QualityTier | undefined;
  savedChoice: QualityTier | undefined;
}): QualityChoice {
  return params.urlOverride ?? params.savedChoice ?? 'auto';
}

/** Resolves the tier a choice applies: an explicit tier as-is, `auto` to the
 * auto-detected tier. */
export function choiceTier(choice: QualityChoice, autoDetectedTier: QualityTier): QualityTier {
  return choice === 'auto' ? autoDetectedTier : choice;
}

export type QualitySwitchAction = 'reload' | 'live' | 'live-partial';

export interface QualitySwitchPlan {
  /**
   * - `live`: apply everything in place, no reload (target antialias already
   *   matches the running context).
   * - `reload`: the switch crosses the antialias boundary; persist the choice
   *   and reload so boot re-creates the WebGL context with the right MSAA.
   * - `live-partial`: the switch crosses the antialias boundary but the choice
   *   could not be persisted (storage unavailable), so a reload can't carry it —
   *   apply every non-antialias setting live and report the shortfall honestly.
   */
  action: QualitySwitchAction;
  /** Honest status-line message for the `live-partial` case; undefined otherwise. */
  message?: string;
}

/** Verbatim status message when an antialias-crossing switch can't be persisted. */
export const STORAGE_UNAVAILABLE_AA_MESSAGE =
  'Antialiasing change needs settings storage; other quality settings applied.';

/**
 * Decides how a switch is carried out. The reload-loop guard is structural: boot
 * resolves the persisted choice BEFORE creating the renderer, so the running
 * context's antialias already matches any persisted choice — planning a switch
 * to that same tier therefore returns `live` (never `reload`), so a user action
 * triggers at most one reload and boot itself never triggers one.
 */
export function planQualitySwitch(params: {
  currentAntialias: boolean;
  targetAntialias: boolean;
  storageOk: boolean;
}): QualitySwitchPlan {
  if (params.currentAntialias === params.targetAntialias) return { action: 'live' };
  if (params.storageOk) return { action: 'reload' };
  return { action: 'live-partial', message: STORAGE_UNAVAILABLE_AA_MESSAGE };
}
